import { Button, Checkbox, Switch } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { format, isBefore, isWithinInterval, parseISO } from 'date-fns';
import React, { ChangeEvent, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { TransitionStatus } from 'react-transition-group';

import { DayPicker } from '../../design-system/Form/DayPicker';
import { Close } from '../../design-system/Icons/Close';
import { Rating } from '../../design-system/Rating';
import { Query } from '../../design-system/types';
import { ActionTypes } from '../../domain';
import { filters } from '../../domain/filters';
import { hideFilters } from '../../resources/filters';
import { countActiveFilters } from '../../resources/filters/operations';
import { getAgentDetailsFromEtsToken } from '../../resources/login/operations';
import { useAuth } from '../../shared/providers/AuthProvider';
import { useTrackEventAmplitude } from '../../shared/analytics';
import { useAppDispatch } from '../../shared/redux';
import { parseQueryString, stringifyQuery } from '../../shared/utilities/queryString';
import {
    useDossierOverviewsDestinations,
    useDossierOverviewsMarkets,
} from '../../resources/pipeline/hooks';
import { useGetAgents } from '../../resources/agent/hooks';

import { FilterContainer } from './components/Container';
import { RangeInput } from './components/RangeInput';
import s from './Filters.css';

type Props = { status: TransitionStatus; today?: Date };

type DateKey = 'arrivalDate' | 'requestDate';

type DateFilter = {
    from?: Date;
    to?: Date;
    selectingTo?: Date;
};

function setInitialValue(value?: string | string[] | null): Date | undefined {
    return typeof value === 'string' ? parseISO(value) : undefined;
}

export function Filters({ status, today = new Date() }: Props): JSX.Element {
    const { trackEvent } = useTrackEventAmplitude();
    const MAX_BUDGET = 15000;
    const etsAgentToken = getAgentDetailsFromEtsToken();
    const { formatMessage, formatNumber } = useIntl();
    const location = useLocation();
    const history = useHistory();
    const query = parseQueryString(location.search) as Query;
    const activeFilters = countActiveFilters(query);
    const [arrivalDate, setArrivalDate] = useState<DateFilter>({
        from: setInitialValue(query.arrivalDateFrom),
        to: setInitialValue(query.arrivalDateTo),
        selectingTo: setInitialValue(query.arrivalDateTo),
    });
    const [requestDate, setRequestDate] = useState<DateFilter>({
        from: setInitialValue(query.requestDateFrom),
        to: setInitialValue(query.requestDateTo),
        selectingTo: setInitialValue(query.requestDateTo),
    });

    const dispatch = useAppDispatch();
    const { userId } = useAuth();
    const { data: agents } = useGetAgents();
    const allAgents = agents ? Object.values(agents) : [];

    const availableMarkets = useDossierOverviewsMarkets();
    const availableDestinations = useDossierOverviewsDestinations();

    const outsideClickRef = useOnclickOutside(() => {
        if (status === 'entered' || status === 'entering') {
            dispatch(hideFilters());
        }
    });

    function resetDayState(setter: React.Dispatch<React.SetStateAction<DateFilter>>): void {
        setter({
            from: undefined,
            to: undefined,
            selectingTo: undefined,
        });
    }

    function pushQueryParams(newQuery: Query | { [key: string]: undefined }): void {
        const search = stringifyQuery({
            ...parseQueryString(location.search),
            ...newQuery,
        });

        trackEvent((ampli, defaultProperties) => {
            ampli.filterSelected({
                ...defaultProperties,
                agency_id: etsAgentToken?.agencyId?.toString() || '',
                filtered_by_agent: search.includes('agentId='),
                filtered_by_budget: search.includes('minBudget=') || search.includes('maxBudget='),
                filtered_by_destination: search.includes('destination='),
                filtered_by_group_6_pax: search.includes('group='),
                filtered_by_rating: search.includes('rating='),
                filtered_by_request_date:
                    search.includes('requestDateTo=') || search.includes('requestDateFrom='),
                filtered_by_solo_traveler: search.includes('solo='),
                filtered_by_start_date_of_services:
                    search.includes('arrivalDateFrom=') || search.includes('arrivalDateTo='),
                filtered_by_to_reply: search.includes('TA='),
                filtered_by_type_of_actions: search.includes('actionType='),
            });
        });
        history.push({
            ...location,
            search: search,
        });
    }

    function handleFilters(e: ChangeEvent<HTMLInputElement>): void {
        const { name, value, checked } = e.currentTarget;

        // The wanted query part can be undefined, a string if there is only
        // 1 value, or an array of values.
        const currentQuerySlice = ([] as string[])
            .concat(query[name] || [])
            .filter((x) => x !== value.toString());
        const newQuery = {
            [name]: checked ? [...currentQuerySlice, value.toString()] : currentQuerySlice,
        };
        pushQueryParams(newQuery);
    }

    function handleAgentFilter(e: ChangeEvent<HTMLInputElement>): void {
        if (!userId) {
            return undefined;
        }
        const { agentId } = query;
        const { checked, value } = e.currentTarget;

        const agentIdFiltered = ([] as string[])
            .concat(agentId || [])
            .filter((agentId) => agentId !== 'all')
            .filter((x) => x !== value.toString());

        const clickOnLoggedUser = value === userId.toString();

        const isCheckingAnotherAgentOnTopOfLoggedUser = !agentId && checked && !clickOnLoggedUser;

        const isUncheckingLoggedUser = clickOnLoggedUser && !checked;

        const isUncheckingLastCheckedAgent = agentId === value && !checked;

        const isNoAgentChecked =
            (!agentId && isUncheckingLoggedUser) || isUncheckingLastCheckedAgent;

        const isOnlyLoggedUserChecked =
            agentIdFiltered.toString() === userId.toString() ||
            (agentId === 'all' && clickOnLoggedUser);

        if (isNoAgentChecked) {
            return pushQueryParams({ agentId: 'all' });
        }

        if (isCheckingAnotherAgentOnTopOfLoggedUser) {
            return pushQueryParams({
                agentId: [userId.toString(), value.toString()],
            });
        }
        if (isOnlyLoggedUserChecked) {
            return pushQueryParams({
                agentId: null,
            });
        }

        pushQueryParams({
            agentId: checked ? [...agentIdFiltered, value.toString()] : agentIdFiltered,
        });
    }

    function handleBudgetChange(values: readonly number[]): void {
        if (values.length === 2 && (values[0] !== 0 || values[1] !== MAX_BUDGET)) {
            pushQueryParams({
                minBudget: values[0].toString(),
                maxBudget: values[1].toString(),
            });
        } else {
            pushQueryParams({
                minBudget: null,
                maxBudget: null,
            });
        }
    }

    function getBudgetValues(): number[] | null {
        if (!query.minBudget || !query.maxBudget) {
            return null;
        }
        return [parseInt(query.minBudget as string, 10), parseInt(query.maxBudget as string, 10)];
    }

    function isChecked(key: string, value: string): boolean {
        if (!userId) {
            return false;
        }
        const queryElement = query[key];
        // We check the logged user checkbox when there is no agentId query params
        if (key === 'agentId' && !queryElement && value === userId.toString()) {
            return true;
        }
        if (!queryElement) {
            return false;
        }
        return queryElement.includes(value.toString());
    }

    function handleDayClick(
        day: Date,
        filterName: DateKey,
        { from, to }: DateFilter,
        setter: React.Dispatch<React.SetStateAction<DateFilter>>,
    ): void {
        const dateFormat = 'yyyy-MM-dd';
        const fromKey = filterName + 'From';
        const toKey = filterName + 'To';

        if (from && to && isWithinInterval(day, { start: from, end: to })) {
            resetDayState(setter);
            return pushQueryParams({
                [fromKey]: null,
                [toKey]: null,
            });
        }
        if (isSelectingFirstDay(from, to, day)) {
            setter({
                from: day,
                to: undefined,
                selectingTo: undefined,
            });
        } else if (from) {
            setter((state) => ({
                ...state,
                to: day,
                selectingTo: day,
            }));
            pushQueryParams({
                [fromKey]: format(from, dateFormat),
                [toKey]: format(day, dateFormat),
            });
        }
    }

    function isSelectingFirstDay(
        dateFrom: Date | null | undefined,
        dateTo: Date | null | undefined,
        day: Date,
    ): boolean {
        const isBeforeFirstDay = dateFrom && isBefore(day, dateFrom);
        const isRangeSelected = Boolean(dateFrom && dateTo);
        return !dateFrom || isBeforeFirstDay || isRangeSelected;
    }

    function handleDayMouseEnter(
        day: Date,
        { from, to }: DateFilter,
        setter: React.Dispatch<React.SetStateAction<DateFilter>>,
    ): void {
        if (!isSelectingFirstDay(from, to, day)) {
            setter((state) => ({
                ...state,
                selectingTo: day,
            }));
        }
    }

    function isAgentFilterActive(): boolean {
        return (query['agentId'] && query['agentId'] !== 'all') || !query['agentId'];
    }

    function clearFilters(): void {
        const filteredQuery = Object.fromEntries(
            Object.entries(query).filter(([key]) => !filters.includes(key)),
        );
        history.push({
            pathname: location.pathname,
            search: stringifyQuery(filteredQuery, { encode: false }),
        });
        resetDayState(setRequestDate);
        resetDayState(setArrivalDate);
    }

    const arrivalDateModifiers = arrivalDate.from &&
        arrivalDate.selectingTo && {
            from: arrivalDate.from,
            to: arrivalDate.selectingTo,
        };
    const requestDateModifiers = requestDate.from &&
        requestDate.selectingTo && {
            from: requestDate.from,
            to: requestDate.selectingTo,
        };

    return (
        <div
            className={cx(s.container, {
                [s.hidden]: status === 'entering',
                [s.visible]: status === 'entered',
            })}
            data-testid="filter-container"
            ref={outsideClickRef}
        >
            <button
                type="button"
                onClick={() => dispatch(hideFilters())}
                className={s.close}
                data-testid="close-button"
            >
                <Close color="var(--ink)" />
            </button>
            <h2 className={s.title}>
                <FormattedMessage id="processes.filters.filters" />
                &nbsp;
                {activeFilters > 0 && (
                    <FormattedMessage
                        id="processes.filters.count"
                        values={{ count: activeFilters }}
                    />
                )}
            </h2>
            <p className={cx(s.subtitle, 'badge', 'ink-light')}>
                <FormattedMessage id="processes.filters.my_dossiers" />
            </p>
            {allAgents.length > 1 && (
                <FilterContainer
                    isActive={isAgentFilterActive()}
                    title={formatMessage({
                        id: 'processes.filters.agents',
                    })}
                >
                    {allAgents.map((value) => (
                        <Checkbox
                            key={value.id}
                            value={value.id}
                            label={value.name}
                            checked={isChecked('agentId', value.id)}
                            onChange={handleAgentFilter}
                            containerClassName={s.line}
                            name="agentId"
                        />
                    ))}
                </FilterContainer>
            )}
            {availableMarkets.length > 1 && (
                <FilterContainer
                    isActive={!!query['market']}
                    title={formatMessage({
                        id: 'processes.filters.market',
                    })}
                >
                    {availableMarkets.map((value) => (
                        <Checkbox
                            key={value}
                            value={value}
                            label={value}
                            checked={isChecked('market', value)}
                            onChange={handleFilters}
                            containerClassName={s.line}
                            name="market"
                        />
                    ))}
                </FilterContainer>
            )}
            {availableDestinations.length > 1 && (
                <FilterContainer
                    isActive={!!query['destination']}
                    title={formatMessage({
                        id: 'processes.filters.destination',
                    })}
                >
                    {availableDestinations.map((value) => (
                        <Checkbox
                            key={value}
                            value={value}
                            label={value}
                            checked={isChecked('destination', value)}
                            onChange={handleFilters}
                            containerClassName={s.line}
                            name="destination"
                        />
                    ))}
                </FilterContainer>
            )}
            <hr className={s.separator} />
            <p className={cx(s.subtitle, 'badge', 'ink-light')}>
                <FormattedMessage id="processes.filters.actions" />
            </p>
            <FilterContainer
                isActive={!!query['actionType']}
                title={formatMessage({
                    id: 'processes.filters.actionType',
                })}
            >
                {ActionTypes.map((value) => (
                    <Checkbox
                        key={value}
                        value={value}
                        label={formatMessage({
                            id: `tasks.action_type.${value}`,
                        })}
                        checked={isChecked('actionType', value)}
                        onChange={handleFilters}
                        containerClassName={s.line}
                        name="actionType"
                    />
                ))}
            </FilterContainer>
            <Switch
                id="TA"
                title={formatMessage({
                    id: 'processes.filters.last_TA',
                })}
                value={1}
                checked={isChecked('TA', '1')}
                onChange={handleFilters}
            />
            <hr className={s.separator} />
            <p className={cx(s.subtitle, 'badge', 'ink-light')}>
                <FormattedMessage id="processes.filters.dossier_criterias" />
            </p>
            <FilterContainer
                isActive={!!query['rating']}
                title={formatMessage({ id: 'processes.filters.rating' })}
            >
                {[1, 2, 3, 4, 5].map((value) => (
                    <Checkbox
                        key={value}
                        value={value}
                        label={<Rating value={value} />}
                        checked={isChecked('rating', value.toString())}
                        onChange={handleFilters}
                        containerClassName={s.line}
                        name="rating"
                    />
                ))}
            </FilterContainer>
            <FilterContainer
                isActive={!!query['arrivalDateFrom']}
                title={formatMessage({
                    id: 'processes.filters.departureDate',
                })}
                style={{ maxHeight: 372 }}
            >
                <DayPicker
                    className="filterDayPicker"
                    selected={
                        arrivalDate.from && arrivalDateModifiers
                            ? [arrivalDate.from, arrivalDateModifiers]
                            : undefined
                    }
                    onDayClick={(day) => {
                        handleDayClick(day, 'arrivalDate', arrivalDate, setArrivalDate);
                    }}
                    modifiers={
                        arrivalDate.from && arrivalDate.selectingTo
                            ? {
                                  start: arrivalDate.from,
                                  end: arrivalDate.selectingTo,
                              }
                            : undefined
                    }
                    onDayMouseEnter={(day) => {
                        handleDayMouseEnter(day, arrivalDate, setArrivalDate);
                    }}
                    defaultMonth={today}
                />
            </FilterContainer>
            <FilterContainer
                isActive={!!query['requestDate']}
                title={formatMessage({
                    id: 'processes.filters.requestDate',
                })}
                style={{ maxHeight: 372 }}
            >
                <DayPicker
                    className="filterDayPicker"
                    selected={
                        requestDate.from && requestDateModifiers
                            ? [requestDate.from, requestDateModifiers]
                            : undefined
                    }
                    onDayClick={(day) => {
                        handleDayClick(day, 'requestDate', requestDate, setRequestDate);
                    }}
                    modifiers={
                        requestDate.from && requestDate.selectingTo
                            ? {
                                  start: requestDate.from,
                                  end: requestDate.selectingTo,
                              }
                            : undefined
                    }
                    onDayMouseEnter={(day) => {
                        handleDayMouseEnter(day, requestDate, setRequestDate);
                    }}
                    defaultMonth={today}
                />
            </FilterContainer>
            <RangeInput
                maxRange={MAX_BUDGET}
                values={getBudgetValues()}
                onChange={handleBudgetChange}
                formatNumber={formatNumber}
            />
            <Switch
                id="solo"
                title={formatMessage({
                    id: 'processes.filters.soloTraveller',
                })}
                value={1}
                checked={isChecked('solo', '1')}
                onChange={handleFilters}
            />
            <Switch
                id="group"
                title={formatMessage({ id: 'processes.filters.group' })}
                value={1}
                checked={isChecked('group', '1')}
                onChange={handleFilters}
            />
            <div className={s.clearContainer}>
                <Button priority="secondary" danger onClick={clearFilters} className={s.clear}>
                    <FormattedMessage id="processes.filters.clearAll" />
                </Button>
            </div>
        </div>
    );
}
